import { Head, router, usePage } from "@inertiajs/react"
import { AlertTriangle, Inbox } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { BrandCoverBand } from "@/components/brand/brand-cover-band"
import { DeclineOverflow } from "@/components/collab-card/decline-overflow"
import { DormancyNudgeBanner } from "@/components/creator/dormancy-nudge-banner"
import { LivePulseTicker } from "@/components/creator/live-pulse-ticker"
import { MissionDropCard } from "@/components/creator/mission-drop-card"
import { RewardTeaserCard } from "@/components/creator/reward-teaser-card"
import { SpottedPostSheet } from "@/components/creator/spotted-post-sheet"
import { TiktokParticipationGate } from "@/components/creator/tiktok-participation-gate"
import { WelcomeHeader } from "@/components/creator/welcome-header"
import { CollabGateAlert } from "@/components/email-verification/collab-gate-alert"
import { EmptyState } from "@/components/empty-state"
import { MissionCard, missionCardStatusFor } from "@/components/mission-card"
import { PromotionalMissionCard } from "@/components/missions/promotional-mission-card"
import { SubmissionsPanel } from "@/components/submissions/submissions-panel"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useCurrentContext } from "@/hooks/use-current-context"
import AppLayout from "@/layouts/app-layout"
import { groupCollabs } from "@/lib/group-collabs"
import { COVER_ASPECT_CLASS } from "@/lib/mission-cover"
import { MOTION_BEATS } from "@/lib/motion"
import type { SharedProps } from "@/types"
import type { Collab, CreatorMissionsProps } from "@/types/collab"
import type { PickerProps, Submission } from "@/types/submissions"

/**
 * OKS-35 — Collab states where the inline `SubmissionsPanel` (TikTok post
 * picker + submitted rows) renders below the MissionCard. OKS-472 KEEPS this
 * subsystem on the home: `creator_submissions_controller` renders
 * `Creator/Missions` + delivers the picker via `only: ["picker"]`, so the
 * picker is architecturally welded to this page (relocating it to §08 would
 * rebuild the shipped OKS-35/391/445 subsystem — out of scope). The MissionCard
 * itself stays CTA-less (UX Spec §2.5); the panel's own pick CTA is the post
 * entry, and the overdue "Post now" opens this same picker.
 */
const SUBMISSION_PANEL_STATES: Collab["state"][] = [
  "accepted",
  "delivered",
  "posted",
]

/**
 * Creator/Missions — §05 Crew Home (OKS-472 rebuild of the OKS-385 W8 home;
 * UX Spec §2). The V2 "living, social" home.
 *
 * Top-to-bottom: DormancyNudgeBanner (OKS-403) → WelcomeHeader (avatar +
 * greeting + streak/views chips) → BrandCoverBand (OKS-304) → LivePulseTicker
 * (OKS-472) → MissionDropCard (OKS-472) → OVERDUE / THIS WEEK / LATER lists of
 * MissionCards → (TabBar rendered by the creator shell).
 *
 * OKS-717 — the welcome now precedes the cover (profile-first; CTPO's binding
 * order). This docstring also previously listed a `BrandStrip` on the page:
 * stale since OKS-701 moved the strip + crew switch to the creator shell.
 * The strip is chrome above this page, not a child of it.
 *
 * ## Tap-through home (OKS-472 KD-4, CTPO-amended to Option B)
 *
 * Each MissionCard is a whole-card link to its §08 detail (no filled CTA — UX
 * Spec §2.5). Accept / apply move to §08's `CTAButton`. Two shipped collab
 * affordances STAY on the home as separate elements (NOT card CTAs), because
 * they are welded to this page and have no §08 surface:
 *   - the inline `SubmissionsPanel` (TikTok picker) for submission-eligible
 *     collabs — `creator_submissions_controller` renders `Creator/Missions` +
 *     `only: ["picker"]`, so relocating it would rebuild OKS-35/391/445;
 *   - the `DeclineOverflow` (Open-`assigned` decline) — a top-right overflow
 *     on the card, unchanged testids.
 * Overdue cards add a ghost "Post now" IFF the row can post — i.e. iff it is
 * submission-eligible (OKS-1004, BRIEF §3 D1). It always opens the inline
 * picker; the "else routes to §08" branch is gone, because the rows it served
 * are exactly the rows that can no longer post.
 *
 * Renders 5 states (UX Spec §3):
 *   - loading  — cover-card-shaped Skeletons (layout-shift-safe); chrome persists
 *   - empty    — Inbox icon + per-reason brand-anchored copy
 *   - error    — destructive Alert + "Try again" partial reload (chrome persists)
 *   - success  — OVERDUE / THIS WEEK / LATER grouped MissionCard lists
 *   - edge     — OVERDUE section first; long titles clamp; dense scroll
 *
 * Driven by Inertia props + local ephemerals (`loading`/`error` are local-only;
 * `online` drives the cached-data banner). OVERDUE / THIS WEEK / LATER grouping
 * is derived client-side (`groupCollabs`) from each collab's deadline/urgency —
 * the backend emits a flat list. Pulse/drop/streak/views are brand-scoped props
 * re-fetched on every crew switch.
 */
type Props = CreatorMissionsProps & {
  _forceLoading?: boolean
  _forceError?: boolean
}

export default function CreatorMissions(props: Props) {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(props._forceLoading ?? false)
  const [error, setError] = useState(props._forceError ?? false)
  // Lazy-init reads navigator.onLine once on mount (SSR-safe).
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  )

  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener("online", on)
    window.addEventListener("offline", off)
    return () => {
      window.removeEventListener("online", on)
      window.removeEventListener("offline", off)
    }
  }, [])

  // OKS-435 — focus-target candidates for the dormancy-banner dismiss. The
  // banner self-unmounts on dismiss, so without an explicit move focus drops
  // to `<body>`. `focusAfterDismiss` resolves the first VISIBLE target and
  // focuses it; `creator-missions-page` is always rendered, so focus always
  // lands somewhere in-flow.
  const pageRef = useRef<HTMLDivElement>(null)
  const readyToSubmitHeadingRef = useRef<HTMLHeadingElement>(null)
  const overdueHeadingRef = useRef<HTMLHeadingElement>(null)
  const thisWeekHeadingRef = useRef<HTMLHeadingElement>(null)
  const laterHeadingRef = useRef<HTMLHeadingElement>(null)
  const emptyStateRef = useRef<HTMLDivElement>(null)
  const errorRef = useRef<HTMLDivElement>(null)

  /**
   * Move keyboard focus into the missions list after the dormancy banner is
   * dismissed (a11y, UX Spec §6 fallback chain). First-match-wins, in reading
   * order: READY TO SUBMIT → OVERDUE → THIS WEEK → LATER heading → empty-state
   * → error alert → page container. Every rung is `tabIndex={-1}`. Focus NEVER
   * falls to `<body>`.
   */
  function focusAfterDismiss() {
    const target =
      readyToSubmitHeadingRef.current ??
      overdueHeadingRef.current ??
      thisWeekHeadingRef.current ??
      laterHeadingRef.current ??
      emptyStateRef.current ??
      errorRef.current ??
      pageRef.current
    target?.focus()
  }

  /**
   * Inertia reload-with-retry for the error state's "Try again" button.
   * `only:` scopes the partial to this page's dynamic body props (incl. the
   * OKS-472 pulse/drop) and leaves shared props alone.
   */
  function retry() {
    setLoading(true)
    setError(false)
    router.reload({
      only: ["collabs", "empty_state", "crew_pulse", "crew_drop"],
      onError: () => setError(true),
      onFinish: () => setLoading(false),
    })
  }

  // OKS-484 (A) — pull the accepted/delivered zero-submission collabs into a
  // pinned READY TO SUBMIT section (above OVERDUE) and REMOVE them from the
  // OVERDUE/THIS WEEK/LATER buckets so a collab appears exactly once. The
  // backend emits the id set (`ready_to_submit_ids`); the FE filters the
  // already-shipped `collabs` list (no duplicate payloads).
  //
  // STICKY for the page mount (seed once via `useState`, NOT recomputed per
  // render). When the inline picker submits (preserveState reload), the collab
  // gains a submission and drops out of a freshly-computed `ready_to_submit_ids`
  // — which would move its card from the READY TO SUBMIT `<ul>` to a LATER
  // `<ul>`, REMOUNTING the `TikTokPostPicker` under a new parent and discarding
  // its `phase:"submitted"` confirmation (the OKS-465 remount class). Pinning
  // the id set keeps the picker mounted in place so the submit completes (panel
  // still updates inline from the refreshed `collabs`/`submissions`); a fresh
  // `/missions` visit remounts the page → re-seeds → the submitted collab
  // correctly leaves the section.
  const [readyToSubmitIds] = useState(
    () => new Set(props.ready_to_submit_ids ?? []),
  )
  const readyToSubmit = props.collabs.filter((c) => readyToSubmitIds.has(c.id))
  const remainingCollabs = props.collabs.filter(
    (c) => !readyToSubmitIds.has(c.id),
  )

  // OKS-472 — derive OVERDUE / THIS WEEK / LATER buckets client-side from the
  // remaining (non-ready-to-submit) collabs. Empty buckets render no header
  // (UX Spec §3).
  const { overdue, thisWeek, later } = groupCollabs(remainingCollabs)

  /** One MissionCard list item (+ optional inline SubmissionsPanel). */
  function renderMissionItem(c: Collab, { isOverdue }: { isOverdue: boolean }) {
    return (
      <MissionsListItem
        key={c.id}
        collab={c}
        isOverdue={isOverdue}
        online={online}
        submissions={props.submissions_by_collab_id?.[c.id] ?? []}
        picker={props.picker}
        streak={props.streak}
        // OKS-637 — mark the card "Action required" iff its collab is in the
        // sticky ready-to-submit set (the SAME signal behind the tab count), so
        // badged-card count reconciles with the Missions-tab total by
        // construction. Uses the sticky Set (not a re-derive) so the badge
        // stays put through an inline-picker submit — mirrors the section.
        actionRequired={readyToSubmitIds.has(c.id)}
      />
    )
  }

  return (
    <AppLayout>
      <Head title={t("creator_home.title")} />
      <div
        ref={pageRef}
        tabIndex={-1}
        className="flex flex-col gap-4 p-4 focus-visible:outline-none"
        data-testid="creator-missions-page"
      >
        {/* OKS-403 — Dormancy re-engagement banner (first child, above the
            cover band). Null unless the backend ships a `dormancy_nudge`. */}
        <DormancyNudgeBanner
          nudge={props.dormancy_nudge}
          onDismissed={focusAfterDismiss}
        />

        {/* OKS-701 — the brand-context strip + crew switch moved to the
            creator shell (rendered once below the navbar on every creator
            page), so the missions home no longer renders its own. */}

        {/* OKS-385 — Welcome header (greeting + streak/views chips).

            OKS-717 — profile-first: the welcome now precedes the cover band
            (CTPO's binding order — the member above the brand). This is a
            LITERAL DOM move, never CSS `order`/`flex-col-reverse` (UX Spec
            §6): CSS ordering would desync keyboard/SR order from the visual
            order. It is also a net a11y win — this block owns the page `<h1>`,
            so the greeting is now announced BEFORE the decorative (`alt=""`)
            cover instead of after it. Keep it above `<BrandCoverBand>`. */}
        <WelcomeHeader
          streak={props.streak}
          brandViewsPulse={props.brand_views_pulse}
          tenure={props.tenure}
          // OKS-539 — map the snake_case wire shape → camelCase RankBlazonInput.
          rankBlazon={
            props.rank_blazon
              ? {
                  name: props.rank_blazon.name,
                  tier: props.rank_blazon.tier,
                  tierCount: props.rank_blazon.tier_count,
                  imageUrl: props.rank_blazon.image_url,
                }
              : null
          }
        />

        {/* OKS-304 — Brand cover hero band. Page chrome on every state.
            OKS-717 — now BELOW the welcome header (see above).
            OKS-716 — the brand identity (logo + name) is now integrated
            onto the cover as a bottom-left overlay, so the disconnected
            shell identity strip is suppressed on this page for single-crew
            creators (see creator-shell.tsx). `identity` is omitted when the
            creator has no active membership (`brand_name` null) → the band
            stays bare. */}
        <BrandCoverBand
          coverUrl={props.brand_cover?.cover_image_url ?? null}
          brandName={props.brand_cover?.brand_name ?? ""}
          identity={
            props.brand_cover?.brand_name
              ? {
                  name: props.brand_cover.brand_name,
                  logoUrl: props.brand_cover.logo_url ?? null,
                }
              : undefined
          }
          className="rounded-xl"
        />

        {/* OKS-472 — Crew Pulse live ticker. Hidden on zero events / absent. */}
        <LivePulseTicker pulse={props.crew_pulse} />

        {/* OKS-472 — New Drop countdown card. Hidden when no upcoming drop. */}
        <MissionDropCard drop={props.crew_drop} />

        {/* OKS-765 — "Next reward to unlock" teaser, above the mission list.
            Renders nothing when there is nothing to tease (reward_teaser null).
            Resolves from initial props → no independent loading/error state. */}
        <RewardTeaserCard
          coinBalance={props.coin_balance ?? 0}
          rewardTeaser={props.reward_teaser ?? null}
        />

        {!online && props.collabs.length > 0 && (
          <Alert data-testid="creator-missions-offline-banner">
            <AlertDescription>
              {t("creator_home.offline.banner")}
            </AlertDescription>
          </Alert>
        )}

        {loading && <MissionsLoadingState />}

        {!loading && error && (
          <Alert
            ref={errorRef}
            tabIndex={-1}
            variant="destructive"
            data-testid="creator-missions-error"
            role="alert"
            className="focus-visible:outline-none"
          >
            <AlertTriangle aria-hidden="true" />
            <AlertTitle>{t("creator_home.error.title")}</AlertTitle>
            <AlertDescription className="mt-2">
              <Button
                variant="outline"
                onClick={retry}
                data-testid="creator-missions-retry"
              >
                {t("creator_home.error.retry")}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!loading &&
          !error &&
          props.collabs.length === 0 &&
          props.empty_state && (
            <div
              ref={emptyStateRef}
              tabIndex={-1}
              className="focus-visible:outline-none"
              data-testid="creator-missions-empty"
            >
              <MissionsEmptyState
                reason={props.empty_state.reason}
                brandName={props.brand_cover?.brand_name ?? null}
              />
            </div>
          )}

        {!loading && !error && props.collabs.length > 0 && (
          <div
            className="flex flex-col gap-6"
            data-testid="creator-missions-list"
          >
            {/* OKS-484 (A) — READY TO SUBMIT — pinned FIRST, above OVERDUE
                (Figma 288:479). accepted/delivered zero-submission collabs;
                each row reuses the shipped MissionCard + inline SubmissionsPanel
                picker ("Submit your post"). Hidden entirely when empty. */}
            {readyToSubmit.length > 0 && (
              <section data-testid="creator-missions-group-ready_to_submit">
                <h2
                  ref={readyToSubmitHeadingRef}
                  tabIndex={-1}
                  className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wide uppercase focus-visible:outline-none"
                >
                  {t("missions.group.ready_to_submit")}
                  <span
                    className="bg-muted text-muted-foreground inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] tabular-nums"
                    data-testid="creator-missions-group-ready_to_submit-count"
                  >
                    {readyToSubmit.length}
                  </span>
                </h2>
                <ul className="flex flex-col gap-3">
                  {readyToSubmit.map((c) =>
                    renderMissionItem(c, { isOverdue: false }),
                  )}
                </ul>
              </section>
            )}

            {/* OVERDUE — own section, first (UX Spec §3 dense edge). */}
            {overdue.length > 0 && (
              <section data-testid="creator-missions-group-overdue">
                <h2
                  ref={overdueHeadingRef}
                  tabIndex={-1}
                  className="text-destructive mb-2 text-xs font-semibold tracking-wide uppercase focus-visible:outline-none"
                >
                  {t("missions.group.overdue")}
                </h2>
                <ul className="flex flex-col gap-3">
                  {overdue.map((c) =>
                    renderMissionItem(c, { isOverdue: true }),
                  )}
                </ul>
              </section>
            )}

            {/* THIS WEEK — due today or due before end of week. */}
            {thisWeek.length > 0 && (
              <section data-testid="creator-missions-group-this_week">
                <h2
                  ref={thisWeekHeadingRef}
                  tabIndex={-1}
                  className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase focus-visible:outline-none"
                >
                  {t("missions.group.this_week")}
                </h2>
                <ul className="flex flex-col gap-3">
                  {thisWeek.map((c) =>
                    renderMissionItem(c, { isOverdue: false }),
                  )}
                </ul>
              </section>
            )}

            {/* LATER — everything else. */}
            {later.length > 0 && (
              <section data-testid="creator-missions-group-later">
                <h2
                  ref={laterHeadingRef}
                  tabIndex={-1}
                  className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase focus-visible:outline-none"
                >
                  {t("missions.group.later")}
                </h2>
                <ul className="flex flex-col gap-3">
                  {later.map((c) => renderMissionItem(c, { isOverdue: false }))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>

      {/*
        OKS-505 (§2d-C) — Spotted-post confirm sheet. A SIBLING OVERLAY (not a
        picker remount) opened by the `?spotted=<id>` deep-link via the
        `spotted_post` page prop. Renders nothing when the prop is absent/null.
      */}
      <SpottedPostSheet spottedPost={props.spotted_post} />
    </AppLayout>
  )
}

/**
 * OKS-472 — one mission list row. The §05 redesign layers the new tap-through
 * `MissionCard` (browse → §08 detail) over the SHIPPED per-collab interaction,
 * which is KEPT on the home (CTPO Option-B ruling — extend, don't break):
 *
 *   - **Promotional collabs** render the shipped `PromotionalMissionCard`
 *     (promo-code copy + buy-on-brand body, OKS-33) — `creator_submissions`
 *     + the promotional body are welded to this page, with no §08 surface.
 *   - **Open/Drop collabs** render the new `MissionCard` (browse, no CTA) +,
 *     when the participate CTA is gated, the shipped email-verification
 *     (`CollabGateAlert`, OKS-146) / TikTok-participation (`TiktokParticipationGate`,
 *     OKS-376) gate that CollabCard used to host.
 *   - **Both** keep the inline `SubmissionsPanel` (TikTok picker) for
 *     submission-eligible states + the `DeclineOverflow` (Open-`assigned`).
 *
 * This wrapper OWNS the picker-open state so the overdue "Post now" and the
 * panel's pick CTA share one source of truth. The MissionCard carries NO
 * submit CTA (UX Spec §2.5), so the panel's own pick CTA is the post entry
 * (no `suppressEmptyStatePickCta`).
 */
function MissionsListItem({
  collab,
  isOverdue,
  online,
  submissions,
  picker,
  streak,
  actionRequired,
}: {
  collab: Collab
  isOverdue: boolean
  online: boolean
  submissions: Submission[]
  picker?: PickerProps
  streak?: { current_days: number } | null
  /** OKS-637 — render the "Action required" badge on this row's card. */
  actionRequired: boolean
}) {
  const { t } = useTranslation()
  const { currentUser } = useCurrentContext()
  const { props: sharedProps } = usePage<Partial<SharedProps>>()
  const [pickerOpen, setPickerOpen] = useState(false)

  const submissionEligible = SUBMISSION_PANEL_STATES.includes(collab.state)
  const openPicker = () => setPickerOpen(true)
  const handleOfflineTap = () => toast(t("creator_home.offline.cta_toast"))

  // OKS-146 / OKS-376 — preserve the shipped participate-CTA gates that lived
  // on CollabCard (replaced here by the CTA-less MissionCard). `ctaIsGated`
  // mirrors CollabCard: accept / apply / submit are the gated kinds.
  const ctaIsGated =
    collab.cta.kind === "accept" ||
    collab.cta.kind === "apply" ||
    collab.cta.kind === "submit"
  const showGateAlert =
    currentUser?.verification_grace_expired === true && ctaIsGated
  const showTiktokGate =
    !showGateAlert &&
    sharedProps.tiktok_association_optional === true &&
    sharedProps.creator_tiktok_connected !== true &&
    ctaIsGated

  const panel = submissionEligible ? (
    <SubmissionsPanel
      collab={collab}
      submissions={submissions}
      picker={picker}
      streak={streak}
      pickerOpen={pickerOpen}
      onPickerOpenChange={setPickerOpen}
    />
  ) : null

  // OKS-33 — Promotional keeps its shipped card (promo body + buy-on-brand);
  // the §05 MissionCard browse layer applies to Open/Drop only.
  if (collab.mission.type === "promotional") {
    return (
      <li className="flex flex-col gap-2">
        <PromotionalMissionCard
          collab={collab}
          offline={!online}
          onOfflineTap={handleOfflineTap}
          onSubmitTap={openPicker}
          actionRequired={actionRequired}
        />
        {panel}
      </li>
    )
  }

  // OKS-1004 (BRIEF §3 D1) — « Publier » renders on an overdue row IFF the
  // row can post.
  //
  // `SUBMISSION_PANEL_STATES` is already this page's single expression of
  // "this row can post": it gates the `SubmissionsPanel` whose picker is what
  // this CTA opens. So the CTA reuses `submissionEligible` rather than
  // enumerating a second list — a copy of a set that already exists, and is
  // load-bearing on this same render, could only drift, and the drift is a
  // CTA that opens a picker which is not on the page.
  //
  // ⚠️ This is deliberately the OPPOSITE discipline from OKS-1002 D1's chip
  // exception ("enumerated, not derived"). There, the exception had no
  // existing expression to reuse and a named set would have recruited members
  // silently. Here the set exists and is the authority. Reuse, do not copy.
  //
  // ⚠️ The `href` variant is GONE, not re-gated: it served exactly the rows
  // that cannot post, so under this rule it has no reachable caller.
  //
  // ⚠️ OKS-1002 D2's `!collab.address_action_required` clause is GONE too,
  // and its guarantee survives: `Collab#address_action_required?` begins
  // `state == "to_ship"`, and `to_ship` is not submission-eligible, so every
  // address-pending row is already suppressed by state. The entailment is
  // asserted at the model layer, in
  // `spec/models/collab_address_action_required_state_oks1004_spec.rb`,
  // rather than left as an inference here — that arm is what makes this
  // removal safe.
  //
  // ⚠️ Suppressed HERE, at the call site, not inside `MissionCard`. This
  // object is built here, and a component that second-guesses a prop its
  // caller owns would silently change `MissionDrop` / `WelcomeBack` too.
  const secondaryAction =
    isOverdue && submissionEligible
      ? { labelKey: "missions.card.post_now", onClick: openPicker }
      : undefined

  // OKS-477 — a locked teaser (future `drops_at`) is a non-interactive card:
  // no secondary action, no decline overflow, no participate gate / picker.
  const locked = Boolean(collab.mission.locked)

  // OKS-1002 (D3) — the ONE thing a locked teaser stops withholding.
  //
  // A pre-`drops_at` card whose creator owes an address gets its chip back and
  // becomes tappable through to the only page that can unblock them. The lock
  // withholds the MISSION'S CONTENT; it must never withhold the creator's own
  // outstanding task.
  //
  // Nothing new is disclosed. The product is withheld ON THE WIRE by
  // `CardSerializer#gift_visible?`, and OKS-986 records that a locked collab's
  // URL is already reachable by typing it — the card's non-tappability was
  // never the gate, so removing it takes nothing away.
  const teaserActionable = locked && collab.address_action_required

  return (
    <li className="flex flex-col gap-2">
      <MissionCard
        collab={collab}
        locked={locked}
        // OKS-637 — "Action required" badge for ready-to-submit collabs. The
        // MissionCard suppresses it on a locked teaser internally.
        actionRequired={actionRequired}
        // OKS-480 — status-driven top-left chip (non-overdue rows); `overdue`
        // keeps the UrgencyChip overlay. `completed` is filtered server-side so
        // it never reaches here. Welded siblings below are all preserved.
        //
        // OKS-1001 — `missionCardStatusFor`, never `collab.state` directly: a
        // `to_ship` collab whose address is the blocker renders the
        // address-pending variant instead of « En préparation ». The helper is
        // the single derivation — see its docstring for why this is not an
        // inline ternary.
        //
        // OKS-1002 (D3) — an ACTIONABLE teaser gets its status back. An
        // ordinary locked teaser still emits none, so its `data-status`
        // attribute stays byte-stable.
        status={
          locked && !teaserActionable ? undefined : missionCardStatusFor(collab)
        }
        overdue={isOverdue}
        lockedActionable={teaserActionable}
        secondaryAction={locked ? undefined : secondaryAction}
        overflow={
          // OKS-472 — Decline stays reachable on the home (no §08 surface).
          // Backend emits `decline_path` only for Open-`assigned` rows; reuses
          // the shipped DeclineOverflow (same testids). Never on a locked teaser.
          !locked && collab.decline_path ? (
            <DeclineOverflow
              declinePath={collab.decline_path}
              missionTitle={collab.mission.title}
            />
          ) : undefined
        }
      />
      {/* OKS-146 / OKS-376 — gate the home participate affordance (the picker
          itself also enforces TikTok/verification; this preserves the shipped
          heads-up message). Email-verification takes precedence. Suppressed on
          a locked drop teaser (OKS-477). */}
      {!locked && showGateAlert ? (
        <CollabGateAlert collabId={collab.id} />
      ) : !locked && showTiktokGate ? (
        <TiktokParticipationGate variant="card" testIdSuffix={collab.id} />
      ) : null}
      {!locked && panel}
    </li>
  )
}

/**
 * Cover-card-shaped Skeleton — matches real MissionCard heights (cover block
 * + title + meta) so there is no layout shift on resolve (UX Spec §3).
 */
function MissionsLoadingState() {
  return (
    <div
      className="flex flex-col gap-3"
      data-testid="creator-missions-loading"
      aria-busy="true"
      aria-live="polite"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="overflow-hidden rounded-xl border"
          data-testid="creator-missions-skeleton"
        >
          <Skeleton className={`${COVER_ASPECT_CLASS} w-full rounded-none`} />
          <div className="flex flex-col gap-2 p-4">
            <Skeleton className="h-5 w-3/4" />
            {/* OKS-986 — the gift row: 48px tile + its two text lines. Sized
                to the real `<GiftLine>` so a card carrying a gift does not
                shift when the data lands. Rendered unconditionally: the
                skeleton cannot know which rows will turn out to be gifting
                missions, and reserving the space is what makes the shift-free
                claim hold for the rows that do (UX Spec §3 Loading). */}
            <div className="flex items-center gap-2.5">
              <Skeleton className="size-12 shrink-0 rounded-lg" />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Skeleton className="h-3 w-14" />
                <Skeleton className="h-4 w-2/5" />
              </div>
            </div>
            {/* OKS-938 — value row: deadline/format left, the two reward
                chips right. Mirrors the real row's layout so the card does
                not reflow when data lands (UX Spec §5 Loading). */}
            <div className="flex items-center justify-between gap-2">
              <Skeleton className="h-4 w-24" />
              <div className="flex shrink-0 items-center gap-1">
                <Skeleton className="h-5 w-14 rounded-full" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            </div>
            {/* OKS-938 — the two bonus lines (≈60% / ≈45% width). */}
            <Skeleton className="h-3 w-3/5" />
            <Skeleton className="h-3 w-2/5" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Empty state — copy differs based on `empty_state.reason`:
 *   - `no_memberships`     → user has no crew memberships yet
 *   - `no_active_missions` → member of a crew, but nothing active
 *
 * OKS-405 — brand-anchored body that names the creator's CURRENT brand when
 * resolvable; a generic `_no_brand` fallback otherwise (never blank, never
 * another brand — brand isolation P0). The "How missions work" CTA is an
 * active ghost button firing a toast (the help route doesn't exist yet).
 * `overlay-enter` plays once on mount (motion-safe).
 */
function MissionsEmptyState({
  reason,
  brandName,
}: {
  reason: "no_active_missions" | "no_memberships"
  brandName: string | null
}) {
  const { t } = useTranslation()
  const description = brandName
    ? t(`creator_home.empty.${reason}.description`, { brand_name: brandName })
    : t(`creator_home.empty.${reason}.description_no_brand`)
  return (
    <EmptyState
      icon={Inbox}
      title={t(`creator_home.empty.${reason}.title`)}
      description={description}
      className={MOTION_BEATS["overlay-enter"]}
      actions={
        <Button
          variant="ghost"
          data-testid="creator-missions-help"
          onClick={() => toast(t("creator_home.empty.help_toast"))}
        >
          {t("creator_home.empty.help_cta")}
        </Button>
      }
    />
  )
}
